import {useState} from 'react';
import {useIntl} from 'react-intl';
import {Loader2, Phone, Video, X} from 'lucide-react';
import type {CallIntent} from '@phonehq/rtc-core';
import {useAppDispatch} from '../../../store/store';
import {showToast} from '../../../store/features/Notifications/notificationsSlice';
import type {UserElement} from '../../../store/features/Users/userService';
import type {TeamMember} from '../../../pages/Team/teamFilters';
import {useDialerActions} from '../dialer/useDialerActions';
import {startTeamGroupCall} from './startTeamCall';
import {teamCallMessages as m} from './teamMessages';

interface TeamSelectionBarProps {
    readonly members: readonly TeamMember[];
    readonly videoAvailable: boolean;
    readonly onClear: () => void;
}

export const TeamSelectionBar = ({members, videoAvailable, onClear}: TeamSelectionBarProps) => {
    const intl = useIntl();
    const dispatch = useAppDispatch();
    const {callTeamMember} = useDialerActions();
    const [starting, setStarting] = useState<CallIntent>();
    const names = members.map(member => [member.firstName, member.lastName].filter(Boolean).join(' ')).filter(Boolean).join(', ');

    const call = async (intent: CallIntent) => {
        if (members.length === 1) {
            const [member] = members;
            callTeamMember({...member, id: Number(member.userId), userId: Number(member.userId)} as unknown as UserElement);
            onClear();
            return;
        }
        setStarting(intent);
        const outcome = await startTeamGroupCall(members.map(member => Number(member.userId)), intent);
        setStarting(undefined);
        if (outcome === 'started') {
            onClear();
        } else if (outcome === 'failed') {
            dispatch(showToast({description: intl.formatMessage(m.failed), type: 'error'}));
        }
    };

    return (
        <div
            role="toolbar"
            aria-label={intl.formatMessage(m.toolbar)}
            data-testid="team-selection-bar"
            className="phq-call-ui flex items-center gap-3 rounded-xl bg-[var(--phq-call-canvas)] px-4 py-2.5 text-[var(--phq-call-ink)] phq-call-shadow-menu"
        >
            <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold">{intl.formatMessage(m.selected, {count: members.length})}</p>
                <p className="truncate text-xs text-[var(--phq-call-ink-muted)]">{names}</p>
            </div>
            <button
                type="button"
                data-testid="team-selection-call"
                disabled={!!starting}
                onClick={() => void call('audio')}
                className="phq-call-press inline-flex h-9 items-center gap-2 rounded-[10px] bg-[var(--phq-call-brand)] px-3 text-sm font-semibold text-white disabled:opacity-60"
            >
                {starting === 'audio' ? <Loader2 size={16} className="animate-spin" aria-hidden="true"/> : <Phone size={16} aria-hidden="true"/>}
                {intl.formatMessage(m.call)}
            </button>
            {videoAvailable && (
                <button
                    type="button"
                    data-testid="team-selection-video"
                    disabled={!!starting}
                    onClick={() => void call('video')}
                    className="phq-call-press inline-flex h-9 items-center gap-2 rounded-[10px] bg-[var(--phq-call-surface-muted)] px-3 text-sm font-semibold disabled:opacity-60"
                >
                    {starting === 'video' ? <Loader2 size={16} className="animate-spin" aria-hidden="true"/> : <Video size={16} aria-hidden="true"/>}
                    {intl.formatMessage(m.video)}
                </button>
            )}
            <button
                type="button"
                aria-label={intl.formatMessage(m.clear)}
                onClick={onClear}
                className="phq-call-press inline-flex h-9 w-9 items-center justify-center rounded-[10px] text-[var(--phq-call-ink-muted)] hover:bg-[var(--phq-call-surface-muted)]"
            >
                <X size={18} aria-hidden="true"/>
            </button>
        </div>
    );
};
